import {
  AutoSaveFeedback,
  ButtonV2,
  LabraToolTipV2,
  ModalConfirmV2,
  ModalSizeVariants,
  PlanType,
} from '@labrav/react-components'

import {
  footerStateType,
  FooterV2,
} from '../../../common/components/FooterV2/FooterV2'
import { useStyles } from './AddEditProductListingFooter.styles'
import {
  FlyoutProductSections,
  FlyoutProductAddEditSectionName,
  FlyOutProductAgreementTerm,
  FlyoutListingProductType,
} from '../../../types/addEditProduct'
import { FormikProps, useFormikContext } from 'formik'
import { LockMessageState } from '../../AddEditProductFormComponents/LockMessageState/LockMessageState'
import { useDispatch, useSelector } from '../../../store/hooks'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  autosaveTimeout,
  awsRequirementsLink,
  awsRequirementsText,
} from '../../../common/utils/constants'
import { isBoolean, isEmpty } from 'lodash'
import { getProductPageMarkerMapping } from '../../../common/utils/getPageMarkerMapping'
import { LockMetadata } from '../../../types/addEditProduct'
import {
  getAllOtherPageMarkersTrue,
  sendFlyoutProduct,
} from '../../../modules/addEditProduct/action'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import clsx from 'clsx'
import { v4 as uuidv4 } from 'uuid'
import { DateTime } from 'luxon'
import initialProduct from '../../../mocks/initialProduct.json'
import { camelize } from 'casing'
import { RequirementsAction } from '../../../common/components/RequirementsAction/RequirementsAction'
import { CloudType } from '../../../types/index'
import { ProductStatus } from '../../../modules/productsListing/types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAws } from '@fortawesome/free-brands-svg-icons'
import { FlyOutProductAgreement } from '../../AddEditProductFormComponents/FlyOutProductAgreement/FlyOutProductAgreement'
import { Typography } from '@mui/material'
import {
  faCircleCheck,
  faDeleteLeft,
  faFileCheck,
} from '@fortawesome/pro-regular-svg-icons'
import {
  getPageMarkerForAutoSave,
  migrationModalText,
  validateProductCreationFlow,
} from '../../../common/utils/productCreationUtil'
import { buyerRegistrationPagePreviewMessage } from '../../../common/utils/messagesConstants'
import {
  BuyerRegistrationPreviewButton,
  BuyerRegistrationPreviewModal,
} from '../../../common/components/BuyerRegistrationPreviewComponents/BuyerRegistrationPreviewComponents'
import {
  startLoading,
  stopLoading,
  LoadingTypes,
  isLoading as Loading,
  useAccountId,
  useCurrentCloudProvider,
  autoSave,
  type AutoSaveTypes,
} from '@labrav/common'
export interface AddEditProductListingFooterProps {
  activeLink?: string
  setActiveLink: React.Dispatch<React.SetStateAction<string>>
  page: FlyoutProductAddEditSectionName
  lockStatus: string
  setLockStatus: React.Dispatch<React.SetStateAction<string>>
  userProfileId: string
  latestLockMetadata: LockMetadata
  isFormLocked: boolean
  productId?: string
  disableTooltipMsg?: string
  handleSubmitForm: (handleSubmit: (status?: string) => void) => void
  isImpartner?: boolean
  hideMultipleSections?: boolean
}

export const AddEditProductListingFooter = ({
  activeLink,
  setActiveLink,
  page,
  lockStatus,
  setLockStatus,
  userProfileId,
  latestLockMetadata,
  isFormLocked,
  productId,
  handleSubmitForm,
  disableTooltipMsg,
  isImpartner,
  hideMultipleSections,
}: AddEditProductListingFooterProps) => {
  const { classes } = useStyles({ isImpartner })
  const navigate = useNavigate()
  const { search } = useLocation()
  const selectedCloudMarketplace = useCurrentCloudProvider()
  const {
    cloud,
    accountId,
    secureKey: secureKeyFromURL,
    partnerId: partnerIdFromURL,
  } = useParams<{
    cloud?: string
    accountId?: string
    secureKey?: string
    partnerId?: string
  }>()
  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const routeAccountId = accountId || fallbackAccountId
  const isLandingPage = activeLink === '/landing-page'
  const formikProps = useFormikContext() as FormikProps<FlyoutProductSections>
  const { values, errors, setValues, setTouched } = formikProps
  const noErrorsForPreview = (errors: any): boolean => {
    const landingPageErrors =
      errors[FlyoutProductAddEditSectionName.LANDING_PAGE_DETAILS]

    return (
      !landingPageErrors?.bgColor &&
      !landingPageErrors?.buttonColor &&
      !landingPageErrors?.buttonTextColor &&
      !landingPageErrors?.headingMessage
    )
  }
  const allValuesFilledForPreview = !noErrorsForPreview(errors)
  const [termsModal, setTermsModal] = useState<boolean>(false)

  const [openModal, setOpenModal] = useState<boolean>(false)

  const handleOpenModal = useCallback(() => {
    setOpenModal(true)
  }, [openModal])

  const handleCloseModal = useCallback(() => {
    setOpenModal(false)
  }, [openModal])

  const initialProductData = camelize(initialProduct)

  const { lockMetadata } = values

  const [counter, setCounter] = useState<number>(0)
  const dispatch = useDispatch()

  const [openClearModal, setOpenClearModal] = useState<boolean>(false)

  const { isLoadingClear } = useSelector<{
    isLoadingClear: boolean
  }>(state => ({
    isLoadingClear: Loading(state.loading)(
      LoadingTypes.CLEAR_FLYOUT_PRODUCT_DETAIL
    ),
  }))

  const handleCloseClearModal = useCallback(() => {
    setOpenClearModal(false)
  }, [setOpenClearModal])

  const { isLoading } = useSelector<{
    isLoading: boolean
  }>(state => ({
    isLoading: Loading(state.loading)(
      LoadingTypes.SUBMIT_FLYOUT_PRODUCT_DETAIL
    ),
  }))

  const isMigrationProduct =
    values[FlyoutProductAddEditSectionName.CLOUD_PRODUCT_DETAILS]
      ?.listingType === FlyoutListingProductType.MIGRATED
  const isMigrationProductListingTab =
    isMigrationProduct &&
    page === FlyoutProductAddEditSectionName.MIGRATION_DETAILS

  const pageMarkerMapping = getProductPageMarkerMapping(isMigrationProduct)
  const isMigrationSinglePageFlow = Boolean(
    hideMultipleSections && isImpartner && isMigrationProduct
  )

  const autoSaveStatus = useSelector<{ status: AutoSaveTypes; error?: string }>(
    state => state.autoSave?.productCreation ?? { status: 'standBy' }
  )

  const productStatus = useSelector(state => {
    if (!productId) return undefined
    const products =
      state.productsListing?.[selectedCloudMarketplace]?.products?.rows ?? []
    return products.find(product => product.productId === productId)?.status
  })

  const [isOpenModalMigrationDetails, setIsOpenModalMigrationDetails] =
    useState<boolean>(false)

  const showMigrationModal = useCallback(() => {
    setIsOpenModalMigrationDetails(true)
  }, [])
  const handleCloseMigration = useCallback(() => {
    setIsOpenModalMigrationDetails(false)
  }, [])

  const isMigratedPage =
    page === FlyoutProductAddEditSectionName.MIGRATION_DETAILS
  const isSelectedProducts =
    isMigratedPage &&
    values[FlyoutProductAddEditSectionName.PAGE_MARKER].page1 !== true

  const descriptionModal = isSelectedProducts
    ? `Confirm selection of  ${
        values[FlyoutProductAddEditSectionName.MIGRATION_DETAILS].products
          ?.length
      } products for migration? \nThe migration process will start upon completion of the product form`
    : migrationModalText

  const checkTerms = (
    values: FlyoutProductSections,
    page: FlyoutProductAddEditSectionName
  ) => {
    const hostWithLabra =
      values[FlyoutProductAddEditSectionName.GENERAL_INFORMATION].hostWithLabra
    if (
      page === FlyoutProductAddEditSectionName.LANDING_PAGE_DETAILS &&
      !hostWithLabra
    ) {
      return true
    }
    if (page === FlyoutProductAddEditSectionName.NOTIFICATIONS_RECIPIENTS) {
      return values[FlyoutProductAddEditSectionName.GENERAL_INFORMATION]
        .termsAndCondition
    }
    if (page === FlyoutProductAddEditSectionName.GENERAL_INFORMATION) {
      const radioCheck = (values?.termsAndConditions?.[page] || []).find(
        term => term.type === 'radio'
      )
      const isItValid =
        (radioCheck?.value && radioCheck?.subTermsWhenFalse?.value) ||
        !radioCheck?.value

      if (!isItValid) {
        return false
      }
    }
    return (values?.termsAndConditions?.[page] || [])
      .filter(term => term.type !== 'radio')
      .every(term => !!term.value)
  }
  const callBackFlyoutProduct = (productId: string) => {
    if (productId) {
      const currentPath = location.pathname
      const isExternalRoute = currentPath.includes('/external-apps/')

      let pathname = `/flyout/aws/${routeAccountId}/products-listing/${productId}/edit`

      if (isExternalRoute) {
        const pathWithSecureKey = `/external-apps/${secureKeyFromURL}/partners/${partnerIdFromURL}/flyout/${cloud}/${accountId}/products-listing/${productId}/edit`
        const pathWithoutSecureKey = `/external-apps/partners/${partnerIdFromURL}/flyout/${cloud}/${accountId}/products-listing/${productId}/edit`
        pathname = secureKeyFromURL ? pathWithSecureKey : pathWithoutSecureKey
      }
      navigate({ pathname: pathname, search }, { replace: true })
    } else {
      setLockStatus('')
    }
  }
  const AUTO_SAVE_STATUSES = [
    ProductStatus.PRODUCT_STATUS_DRAFT,
    ProductStatus.PRODUCT_STATUS_CREATION_FAILED,
    ProductStatus.PRODUCT_STATUS_MIGRATION_IN_QUEUE,
    ProductStatus.MIGRATION_FAILED,
  ]

  const autoSaving = useCallback(async () => {
    if (isMigrationProductListingTab) {
      return
    }
    if (
      productId &&
      productStatus &&
      !AUTO_SAVE_STATUSES.includes(productStatus)
    ) {
      navigate(`/flyout/aws/${routeAccountId}/products-listing`)
      return
    }
    if (routeAccountId) {
      const isErrorInCurrentPage = validateProductCreationFlow(
        page,
        formikProps
      )
      const currentPageMarker = pageMarkerMapping[page]
      const markerChanges = {
        [currentPageMarker]: !isErrorInCurrentPage && checkTerms(values, page),
      }
      const updateProduct = {
        ...values,
        [FlyoutProductAddEditSectionName.PAGE_MARKER]: {
          ...values[FlyoutProductAddEditSectionName.PAGE_MARKER],
          ...getPageMarkerForAutoSave(formikProps),
          ...(currentPageMarker ? markerChanges : {}),
        },
        lockMetadata: {
          ...values.lockMetadata,
          windowId: latestLockMetadata.windowId,
          lockedBy: latestLockMetadata.lockedBy,
          lockedByName: latestLockMetadata.lockedByName,
          lockByEmail: latestLockMetadata.lockByEmail,
          lockAcquisitionProbe:
            isFormLocked || isEmpty(productId) ? true : undefined,
        },
      }
      await dispatch(
        autoSave('productCreation', async () => {
          return await dispatch(
            sendFlyoutProduct({
              partnerId: routeAccountId,
              isAutoSave: true,
              productForm: updateProduct,
              pageMarkerMapping,
              page,
              callBackFlyoutProduct,
              productId,
            })
          )
        })
      )
    }
  }, [
    values,
    routeAccountId,
    errors,
    productId,
    latestLockMetadata,
    page,
    pageMarkerMapping,
    productStatus,
    navigate,
    isMigrationProductListingTab,
  ])

  useEffect(() => {
    if (values && !counter && productId) {
      const { pageMarker } = values
      const isMigrated =
        values[FlyoutProductAddEditSectionName.CLOUD_PRODUCT_DETAILS]
          ?.listingType === FlyoutListingProductType.MIGRATED
      if (!isImpartner) {
        if (!pageMarker?.page1) {
          setActiveLink('/product-details')
        } else if (!pageMarker?.page2 && !isMigrated) {
          setActiveLink('/product-dimensions')
        } else if (!pageMarker?.page3) {
          setActiveLink('/landing-page')
        } else if (!pageMarker?.page4) {
          setActiveLink('/notification-recipients')
        } else {
          setActiveLink('/product-details')
        }
      }
      if (isImpartner) {
        setActiveLink('/product-details')
      }
    }
  }, [counter, productId])

  useEffect(() => {
    const interval = setInterval(() => {
      if (
        selectedCloudMarketplace === 'AWS' &&
        !isImpartner &&
        !isMigrationProductListingTab
      ) {
        setCounter(counter + 1)
      }
    }, autosaveTimeout)
    return () => clearInterval(interval)
  })

  useEffect(() => {
    if (
      counter &&
      selectedCloudMarketplace === 'AWS' &&
      !isImpartner &&
      !isMigrationProductListingTab
    ) {
      autoSaving()
    }
  }, [counter, selectedCloudMarketplace, isMigrationProductListingTab])

  const migrationProductCount = values?.migrationDetails?.products?.length

  const currentPath = location.pathname

  let pathname = `/flyout/aws/${routeAccountId}/products-listing`

  if (isImpartner) {
    const secureKey = secureKeyFromURL ? secureKeyFromURL : ''
    const pathWithSecureKey = `/external-apps/${secureKey}/partners/${partnerIdFromURL}/flyout/${cloud}/${accountId}/products-listing/`
    const pathWithoutSecureKey = `/external-apps/partners/${partnerIdFromURL}/flyout/${cloud}/${accountId}/products-listing/`
    pathname = secureKeyFromURL ? pathWithSecureKey : pathWithoutSecureKey
  }
  const redirectToListing = () => navigate(pathname, { replace: true })

  const handleSubmitApiCall = async ({
    accountId,
    status,
    isSinglePageFinalSubmit,
  }: {
    accountId?: string
    status?: string
    isSinglePageFinalSubmit?: boolean
  }) => {
    await handleCloseModal()
    await setTermsModal(false)
    await handleCloseMigration()
    if (accountId) {
      const isErrorInCurrentPage = validateProductCreationFlow(
        page,
        formikProps
      )
      const currentPageMarker = pageMarkerMapping[page]

      const updateProduct = {
        ...values,
        [FlyoutProductAddEditSectionName.PAGE_MARKER]: {
          ...values[FlyoutProductAddEditSectionName.PAGE_MARKER],
          [currentPageMarker]:
            !isErrorInCurrentPage && checkTerms(values, page),
        },
      }
      const isLockEmpty =
        isEmpty(updateProduct?.lockMetadata?.lockedBy) ||
        isEmpty(updateProduct?.lockMetadata?.windowId)
      if (status === 'lockAcquire' || isLockEmpty) {
        const newWindowId = uuidv4()
        const newLastActivityAt = DateTime.now().toUTC().toISO()
        window.name = newWindowId

        updateProduct.lockMetadata = {
          ...updateProduct.lockMetadata,
          windowId: newWindowId,
          lockedBy: userProfileId,
          lastActivityAt: newLastActivityAt,
          lockedByName: latestLockMetadata.lockedByName,
          lockByEmail: latestLockMetadata.lockByEmail,
          lockAcquisitionProbe: isLockEmpty ? undefined : true,
        }
      } else {
        updateProduct.lockMetadata = {
          ...updateProduct.lockMetadata,
          lockAcquisitionProbe: undefined,
        }
      }

      await dispatch(
        sendFlyoutProduct({
          partnerId: accountId,
          isAutoSave: false,
          productForm: updateProduct,
          pageMarkerMapping,
          page,
          callBackFlyoutProduct,
          productId,
          status,
          redirectToListing,
          switchLandingPage: allOtherPageMarkersTrue && isImpartner,
          isSinglePageFinalSubmit: isSinglePageFinalSubmit,
        })
      )
      const nextPage = await getNextPage(page)
      if (nextPage && isEmpty(status) && !isMigrationSinglePageFlow) {
        await setActiveLink(nextPage)
      }
    }
  }

  const allOtherPageMarkersTrue = getAllOtherPageMarkersTrue({
    pageMarker: values[FlyoutProductAddEditSectionName.PAGE_MARKER],
    pageMarkerMapping,
    sectionName: page,
    isMigrated:
      values[FlyoutProductAddEditSectionName.CLOUD_PRODUCT_DETAILS]
        ?.listingType === FlyoutListingProductType.MIGRATED,
  })

  const handleSubmit = async (status?: string) => {
    if (!accountId) {
      return
    }

    if (isMigrationSinglePageFlow) {
      handleOpenModal()
      return
    }

    if (isMigratedPage && !isOpenModalMigrationDetails) {
      showMigrationModal()
    } else {
      handleCloseMigration()
      if (allOtherPageMarkersTrue) {
        setTermsModal(true)
      } else {
        await handleSubmitApiCall({ accountId, status })
      }
    }
  }

  const handleClickSubmit = () => {
    handleSubmitForm(handleSubmit)
  }

  const [open, setOpen] = useState<boolean>(false)
  const handleClose = () => {
    setOpen(false)
  }
  const handlePreview = () => {
    setOpen(true)
  }

  const shouldAgreementDisabled: boolean = useMemo(() => {
    const data = (values?.agreement?.terms || [])?.map(
      (term: FlyOutProductAgreementTerm) => {
        if (term.type === 'checkbox') {
          return !term.value
        } else if (term.type === 'radio') {
          if (term.value === true && term.subTermsWhenTrue) {
            return !term.subTermsWhenTrue.value
          } else if (term.value === false && term.subTermsWhenFalse) {
            return !term.subTermsWhenFalse.value
          }
        }
        return false
      }
    )
    return data.includes(true)
  }, [values.agreement.terms])

  const handleClearPage = () => {
    switch (page) {
      case FlyoutProductAddEditSectionName.GENERAL_INFORMATION:
        return {
          ...values,
          generalInformation: {
            ...initialProductData.generalInformation,
            multipleDimensionsAllowed:
              values.generalInformation.multipleDimensionsAllowed,
            countryAvailability: values.generalInformation.countryAvailability,
            excludedCountries: values.generalInformation.excludedCountries,
            includedCountries: values.generalInformation.includedCountries,
            headerImg: values.generalInformation.headerImg,
            buyerNotificationsAllowed:
              values.generalInformation.buyerNotificationsAllowed,
            termsAndCondition: values.generalInformation.termsAndCondition,
          },
          infoCollectionFormMetaData: {
            ...initialProductData.infoCollectionFormMetaData,
            productLogoUrl: '',
          },
          termsAndConditions: {
            ...values.termsAndConditions,
            generalInformation:
              initialProductData?.termsAndConditions?.generalInformation,
          },
        }

      case FlyoutProductAddEditSectionName.PRODUCT_DIMENSIONS:
        return {
          ...values,
          productDimensions: initialProductData.productDimensions,
          generalInformation: {
            ...values.generalInformation,
            multipleDimensionsAllowed: null,
            countryAvailability: 'all',
            excludedCountries: '',
            includedCountries: '',
          },
          productDetails: {
            ...values.productDetails,
            productType: PlanType.SAAS_CONTRACT,
          },
          termsAndConditions: {
            ...values.termsAndConditions,
            productDimensions:
              initialProductData?.termsAndConditions?.productDimensions,
          },
        }

      case FlyoutProductAddEditSectionName.LANDING_PAGE_DETAILS:
        return {
          ...values,
          infoCollectionFormMetaData: {
            ...initialProductData.infoCollectionFormMetaData,
            productLogoUrl: values.generalInformation.productLogoUrl,
          },
          termsAndConditions: {
            ...values.termsAndConditions,
            infoCollectionFormMetaData:
              initialProductData?.termsAndConditions
                ?.infoCollectionFormMetaData,
          },
        }

      case FlyoutProductAddEditSectionName.NOTIFICATIONS_RECIPIENTS:
        return {
          ...values,
          notificationRecipients: initialProductData.notificationRecipients,
          generalInformation: {
            ...values.generalInformation,
            headerImg: '',
            buyerNotificationsAllowed: true,
            termsAndCondition: false,
          },
        }

      case FlyoutProductAddEditSectionName.MIGRATION_DETAILS:
        return {
          ...values,
          migrationDetails: {
            products: [],
          },
        }
      default:
        break
    }
  }

  const resetFormData = async () => {
    dispatch(startLoading(LoadingTypes.CLEAR_FLYOUT_PRODUCT_DETAIL))

    try {
      const productData = handleClearPage()
      await setValues(productData as FlyoutProductSections)
      await setTouched({})

      if (!isImpartner) {
        await restFormApiCall(productData as FlyoutProductSections)
      }

      setOpenClearModal(false)
    } finally {
      dispatch(stopLoading(LoadingTypes.CLEAR_FLYOUT_PRODUCT_DETAIL))
    }
  }
  const restFormApiCall = useCallback(
    async (productData: FlyoutProductSections) => {
      if (
        productId &&
        productStatus &&
        !AUTO_SAVE_STATUSES.includes(productStatus)
      ) {
        navigate(`/flyout/aws/${routeAccountId}/products-listing`)
        return
      }
      if (routeAccountId) {
        const currentPageMarker = pageMarkerMapping[page]
        const updatedData = {
          ...productData,
          [FlyoutProductAddEditSectionName.PAGE_MARKER]: {
            ...values[FlyoutProductAddEditSectionName.PAGE_MARKER],
            [currentPageMarker]: false,
          },
          lockMetadata: {
            ...values.lockMetadata,
            windowId: latestLockMetadata.windowId,
            lockedBy: latestLockMetadata.lockedBy,
            lockedByName: latestLockMetadata.lockedByName,
            lockByEmail: latestLockMetadata.lockByEmail,
            lockAcquisitionProbe:
              isFormLocked || isEmpty(productId) ? true : undefined,
          },
        }
        await dispatch(
          autoSave('productCreation', async () => {
            return await dispatch(
              sendFlyoutProduct({
                partnerId: routeAccountId,
                isAutoSave: true,
                productForm: updatedData,
                pageMarkerMapping,
                page,
                callBackFlyoutProduct,
                productId,
                isClear: true,
              })
            )
          })
        )
      }
    },
    [
      values,
      routeAccountId,
      errors,
      productId,
      page,
      latestLockMetadata,
      isFormLocked,
      productStatus,
      navigate,
    ]
  )
  const footerLeft = !autoSaveStatus || autoSaveStatus.status === 'standBy'

  const hostWithLabra =
    values?.[FlyoutProductAddEditSectionName.GENERAL_INFORMATION].hostWithLabra
  const isHostWithLabra = isBoolean(hostWithLabra) ? hostWithLabra : true

  const hideActions =
    !isHostWithLabra &&
    page === FlyoutProductAddEditSectionName.LANDING_PAGE_DETAILS

  return (
    <div className={!hideMultipleSections ? classes.footerWrapper : ''}>
      <FooterV2
        actions={
          <div
            className={clsx(
              classes.innerfooterWrapper,
              footerLeft && lockStatus === ''
                ? classes.innerfooterWrapperLeft
                : '',
              selectedCloudMarketplace === 'AZURE' ? classes.footerRight : '',
              hideMultipleSections ? classes.footerWrapperIframe : ''
            )}
          >
            {selectedCloudMarketplace === 'AWS' && (
              <>
                {lockStatus !== '' ? (
                  <LockMessageState
                    sinceTime={lockMetadata?.lastActivityAt}
                    lockByName={lockMetadata?.lockedByName}
                    lockStatus={lockStatus}
                    setLockStatus={setLockStatus}
                    userId={userProfileId as string}
                    lockedBy={lockMetadata?.lockedBy}
                    lockByEmail={lockMetadata?.lockByEmail}
                  />
                ) : !autoSaveStatus || autoSaveStatus.status === 'standBy' ? (
                  <></>
                ) : (
                  <div>
                    <AutoSaveFeedback status={autoSaveStatus.status} />
                  </div>
                )}
              </>
            )}
            <div
              data-testid={'add-edit-product-footer'}
              className={classes.buttonWrapper}
            >
              {page === FlyoutProductAddEditSectionName.MIGRATION_DETAILS && (
                <p className={classes.productCount}>
                  {migrationProductCount} products selected
                </p>
              )}
              {lockStatus !== footerStateType.UNLOCKED ? (
                <LabraToolTipV2
                  tooltipPlacement="top-end"
                  description={disableTooltipMsg}
                  tooltipPlacmentGap="32px"
                >
                  <div className={classes.buttonWrapper}>
                    {!hideActions ? (
                      <>
                        {!isMigrationProduct && (
                          <ButtonV2
                            data-testid={'clear-page'}
                            styleType="link"
                            onClick={() => setOpenClearModal(true)}
                            disabled={isFormLocked}
                          >
                            Clear page
                          </ButtonV2>
                        )}
                        {isLandingPage && (
                          <div
                            className={isFormLocked ? classes.disableBtn : ''}
                          >
                            <BuyerRegistrationPreviewButton
                              isFormLocked={isFormLocked}
                              allValuesFilledForPreview={
                                allValuesFilledForPreview
                              }
                              buyerRegistrationPagePreviewMessage={
                                buyerRegistrationPagePreviewMessage
                              }
                              handlePreview={handlePreview}
                            />
                          </div>
                        )}
                      </>
                    ) : (
                      <></>
                    )}
                    <div className={isFormLocked ? classes.disableBtn : ''}>
                      <ButtonV2
                        data-testid="submit-button"
                        styleType="primary"
                        onClick={handleClickSubmit}
                        disabled={isFormLocked}
                        loading={isLoading}
                      >
                        {allOtherPageMarkersTrue ? 'Submit' : 'Continue'}
                      </ButtonV2>
                    </div>
                  </div>
                </LabraToolTipV2>
              ) : (
                <>
                  <ButtonV2
                    data-testid="start-editing"
                    styleType="primary"
                    onClick={() => handleSubmit('lockAcquire')}
                    loading={isLoading}
                  >
                    Start editing
                  </ButtonV2>
                </>
              )}
            </div>
          </div>
        }
        {...(selectedCloudMarketplace === 'AWS'
          ? { footerState: lockStatus }
          : {})}
        width={isImpartner ? 'calc(100% + 78px)' : 'calc(100% - 352px)'}
        customClass={classes.root}
      />
      <ModalConfirmV2
        title={'I adhere to the following AWS requirements'}
        open={termsModal}
        titleAcceptButton={'Submit'}
        titleCancelButton={'Back'}
        miscellaneousActions={
          <RequirementsAction
            link={awsRequirementsLink}
            linkText={awsRequirementsText}
            cloudType={CloudType.AWS}
          />
        }
        onAccept={() => handleOpenModal()}
        onDecline={() => setTermsModal(false)}
        variant={ModalSizeVariants.xLarge}
        acceptDisabled={shouldAgreementDisabled}
        // helpButton={true}
        content={<FlyOutProductAgreement />}
        icon={
          <FontAwesomeIcon
            icon={faAws}
            style={{ height: '16px', width: '16px' }}
          />
        }
      />

      <ModalConfirmV2
        title={isImpartner ? 'Migrate listing?' : 'Submit listing?'}
        description={
          <Typography className={classes.modalText}>
            {isImpartner ? (
              <>
                <p>
                  Once migrated, this listing will be available for your
                  partners to create Marketplace Private Offers after you
                  approve them.
                </p>
                <p>Are you sure you want to proceed?</p>
              </>
            ) : (
              <p>Are you sure you want to submit your listing?</p>
            )}
            {!isImpartner && (
              <>
                <p>
                  Your listing will be created and sent for testing to Labra
                  team if you submit it now. Once Labra Testing is completed, it
                  will be shared with AWS team to be tested and made public on
                  AWS Marketplace.
                </p>
                <p>
                  <span className={classes.nameBoldText}>Note: </span>
                  You can continue to edit except when it is sent to AWS team
                  for testing. You can also edit your listing once it is Public
                  on AWS Marketplace.
                </p>
              </>
            )}
          </Typography>
        }
        open={openModal}
        titleAcceptButton={'Confirm'}
        onAccept={() =>
          handleSubmitApiCall({
            accountId: routeAccountId,
            isSinglePageFinalSubmit: isMigrationSinglePageFlow,
          })
        }
        onDecline={handleCloseModal}
        variant={ModalSizeVariants.small}
        icon={
          <FontAwesomeIcon
            icon={faFileCheck}
            style={{ width: '20px', height: '16px' }}
          />
        }
        hideCancelIcon={false}
        hideCancelButton={false}
      />

      <ModalConfirmV2
        title={'Clear page?'}
        description={
          <Typography className={classes.clearModalText}>
            <p>
              All the data filled in this page will be removed. You will have an
              empty page with blank fields which will have to be filled again.
            </p>
            <p>Are you sure you want to clear this page?</p>
          </Typography>
        }
        open={openClearModal}
        titleAcceptButton={'Clear page'}
        onAccept={resetFormData}
        onDecline={handleCloseClearModal}
        variant={ModalSizeVariants.medium}
        icon={
          <FontAwesomeIcon
            icon={faDeleteLeft}
            style={{ width: '18px', height: '16px' }}
          />
        }
        acceptLoading={isLoadingClear}
        hideCancelIcon={false}
        hideCancelButton={false}
      />

      <ModalConfirmV2
        title={isSelectedProducts ? 'Confirm selection' : 'Save changes?'}
        description={descriptionModal}
        open={isOpenModalMigrationDetails}
        titleAcceptButton={isSelectedProducts ? 'Confirm' : 'Save'}
        onAccept={handleSubmit}
        onDecline={handleCloseMigration}
        variant={ModalSizeVariants.medium}
        acceptDisabled={false}
        icon={
          <FontAwesomeIcon
            icon={faCircleCheck}
            style={{ width: '20px', height: '16px' }}
          />
        }
      />

      <BuyerRegistrationPreviewModal open={open} handleClose={handleClose} />
    </div>
  )
}

export const getNextPage = (sectionName: FlyoutProductAddEditSectionName) => {
  if (sectionName === FlyoutProductAddEditSectionName.GENERAL_INFORMATION) {
    return '/product-dimensions'
  } else if (
    sectionName === FlyoutProductAddEditSectionName.PRODUCT_DIMENSIONS
  ) {
    return '/landing-page'
  } else if (
    sectionName === FlyoutProductAddEditSectionName.LANDING_PAGE_DETAILS
  ) {
    return '/notification-recipients'
  } else if (
    sectionName === FlyoutProductAddEditSectionName.MIGRATION_DETAILS
  ) {
    return '/landing-page'
  }
}
